import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardActions, 
  Button,
  Divider,
  CircularProgress,
  TextField,
  Paper,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAppDispatch } from '../hooks/redux-hooks';
import { searchOrderById } from '../slices/authSlice';
import { serviceType } from '../constants';
import { getOrderStatusText } from '../utils/orderUtils';

const OrderSearch = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [order, setOrder] = useState<any>(null);
  const [error, setError] = useState('');

  const handleSearch = async () => {
    if (!orderId.trim()) {
      setError(t('pleaseEnterOrderId'));
      return;
    }

    setLoading(true);
    setError('');
    setOrder(null); // 清空之前的搜索结果
    
    try {
      const result = await dispatch(searchOrderById({ order_id: +orderId, is_guest: true }) as any).unwrap();
      if (result) {
        setOrder(result);
      } else {
        setError(t('orderNotFound'));
      }
    } catch (err) {
      setError(t('searchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (order: any) => {
    navigate('/curorder', { state: { orderDetail: order } });
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton
          edge="start"
          color="inherit"
          onClick={handleBack}
          aria-label="back"
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" align="center" sx={{ flex: 1 }}>
          {t('searchOrder')}
        </Typography>
      </Box>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            label={t('orderId')}
            value={orderId}
            onChange={(e) => {
                const value = e.target.value;
                // 只允许输入数字
                if (value === '' || /^\d+$/.test(value)) {
                  setOrderId(value);
                }
              }}
            error={!!error}
            helperText={error}
            inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*'
            }}
          />
          <Button 
            size="small"
            variant="contained" 
            onClick={handleSearch}
            disabled={loading}
            sx={{ minWidth: '80px', height: '55px' }}
          >
            {loading ? <CircularProgress size={20} /> : t('search')}
          </Button>
        </Box>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {order && !loading && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {order.service_type === serviceType.AIRPORT_PICK_UP 
                ? t('pickupService') 
                : t('dropoffService')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('orderId')}: {order.order_id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('labelOrderDate')}: {new Date(order.created_at).toLocaleString()}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body1">
              {t('status')}: {getOrderStatusText(order.status, t)}
            </Typography>
            <Typography variant="body1">
              {t('name')}: {order.customer_name}
            </Typography>
            {order.service_type === serviceType.AIRPORT_PICK_UP ? (
              <>
                <Typography variant="body1">
                  {t('pickupAirport')}: {order.airport}
                </Typography>
                <Typography variant="body1">
                  {t('dropOffLocation')}: {order.addr1}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body1">
                  {t('pickupLocation')}: {order.addr1}
                </Typography>
                <Typography variant="body1">
                  {t('dropOffAirport')}: {order.airport}
                </Typography>
              </>
            )}
          </CardContent>
          <CardActions>
            <Button 
              size="small" 
              variant="contained"
              onClick={() => handleViewDetails(order)}
            >
              {t('viewDetails')}
            </Button>
          </CardActions>
        </Card>
      )}
    </Box>
  );
};

export default OrderSearch;